import { getAuth } from "firebase/auth";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { INTENTS } from "~/utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import * as APITypes from "~/API";
import { PrecomputedAmenityData } from "~/types";
import { apiClient, AmenityAddReservationLoaderResult } from "~/services/apiClient";

dayjs.extend(utc);

function transformToPrecomputed(
  raw: AmenityAddReservationLoaderResult["amenityData"]
): PrecomputedAmenityData {
  return {
    formattedAmenityDetails: {
      ...raw.formattedAmenityDetails,
      reservations: raw.formattedAmenityDetails.reservations.map((r) => ({
        timeFrame: {
          start: dayjs(r.timeFrame.start),
          end: dayjs(r.timeFrame.end),
        },
        createdAt: dayjs(r.createdAt),
        data: r.data,
      })),
    },
    availability: {
      available: (() => {
        const days = raw.availability.available.slice(0, 7);
        while (days.length < 7) days.push([]);
        return days.map((day) =>
          day.map((tf) => ({
            start: dayjs(tf.start),
            end: dayjs(tf.end),
          }))
        ) as PrecomputedAmenityData["availability"]["available"];
      })(),
      unavailable: raw.availability.unavailable.map((u) => ({
        timeFrame: {
          start: dayjs(u.timeFrame.start),
          end: dayjs(u.timeFrame.end),
        },
        reason: u.reason,
      })),
      fullyUnavailable: raw.availability.fullyUnavailable,
    },
  };
}

export const AddReservationPageLoader = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<AddReservationPageLoaderType | Response> => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return redirect("/explore");

  const amenityID = params.amenityID || "";
  if (!amenityID) return redirect("/amenity");

  const response = await apiClient.loader<AmenityAddReservationLoaderResult>(
    "amenity-add-reservation",
    { id: amenityID }
  );

  if (!response.success || !response.data) {
    console.error(response.error);
    return redirect("/amenity");
  }

  const amenity = transformToPrecomputed(response.data.amenityData);

  return { amenity, userID: user.uid };
};

export type AddReservationPageLoaderType = {
  amenity: PrecomputedAmenityData;
  userID: string;
};

export const AddReservationPageAction = async ({
  request,
  params,
}: LoaderFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const auth = getAuth();
  const user = auth.currentUser;
  const userID = user?.uid || "";
  const amenityID = params.amenityID || "";

  // TODO: CS - create mutex, then check if a reservation already exists,
  // also check that maxRequests will not be exceeded, then
  // create a new reservation if not and release lock
  // TODO: this should be a transaction

  if (amenityID !== "" && intent === INTENTS._amenity_create_reservation) {
    const payload = JSON.parse(formData.get("body") as string) as {
      details?: string;
      status?: APITypes.AmenityReservationStatus;
      startDate: string;
      endDate: string;
      partySize?: number;
    };

    const result = await apiClient.sendIntent<APITypes.AmenityReservation>(
      INTENTS._amenity_create_reservation,
      {
        amenityID,
        tenantID: userID,
        ...payload,
      }
    );

    if (result.success && result.data) {
      return redirect(`/amenity/${amenityID}?tab=reservations`);
    }

    return {
      success: false,
      error: result.error ?? "Failed to create reservation",
    };
  }
  return {};
};
